import React, { useState } from 'react';
import { Upload, Loader2, CheckCircle2 } from 'lucide-react';
import StorageManager from '../services/StorageManager';
import MuxUploader from '../services/MuxUploader';
import { Icon } from '../components/Icons';

function CreatorUploadNew({ currentUser, onHome, onBack, onSubmit }) {
  const [formData, setFormData] = useState({ pilotTitle: '', logline: '', genre: '', playbackId: '' });
  const [videoFile, setVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleVideoUpload = async (file) => {
    setVideoFile(file);
    setUploadStatus('uploading');
    setError('');
    try {
      const { uploadUrl, uploadId } = await MuxUploader.getUploadUrl();
      await MuxUploader.uploadVideo(file, uploadUrl, setUploadProgress);
      setUploadStatus('processing');
      const { playbackId } = await MuxUploader.waitForPlaybackId(uploadId);
      setFormData(prev => ({ ...prev, playbackId }));
      setUploadStatus('ready');
    } catch (err) {
      console.error('Upload error:', err);
      setError('Video upload failed. Please try again.');
      setUploadStatus('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.playbackId) { setError('Please upload a video first'); return; }
    setSubmitting(true);
    setError('');
    try {
      const pilotData = {
        pilotTitle: formData.pilotTitle,
        logline: formData.logline,
        genre: formData.genre,
        playbackId: formData.playbackId,
        creatorUserId: currentUser.id,
        creatorName: currentUser.displayName || currentUser.username || currentUser.name,
      };
      console.log('Submitting pilot:', pilotData);
      const result = await StorageManager.savePilot(pilotData);
      console.log('Save result:', result);
      if (result && result.success) {
        onSubmit();
      } else {
        setError('Failed to submit pilot: ' + (result?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Pilot submission error:', err);
      setError('Failed to submit pilot: ' + err.message);
    }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: '100vh', padding: '2rem 3rem', fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff', background: '#000000' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
      ` }} />
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
        <button onClick={onHome}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '0.75rem 1.25rem', color: 'rgba(255,255,255,0.45)',
            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.88rem', transition: 'all 0.25s ease' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          Home
        </button>
        <button onClick={onBack}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '10px', padding: '0.75rem 1.25rem', color: 'rgba(255,255,255,0.45)',
            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.88rem', transition: 'all 0.25s ease' }}>
          ← Back to My Pilots
        </button>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 400, marginBottom: '2rem', textAlign: 'center', letterSpacing: '0.1em',
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          SUBMIT YOUR PILOT
        </h1>

        {error && <div style={{ background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.3)',
          borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem', color: '#ff6b6b' }}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ background: 'rgba(255,255,255,0.03)', padding: '2rem', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
          {/* Video upload */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.75rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)' }}>Video</label>
            {uploadStatus === 'idle' && (
              <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem',
                background: 'rgba(255,255,255,0.03)', border: '2px dashed rgba(255,255,255,0.2)', borderRadius: '12px', cursor: 'pointer' }}>
                <Icon component={Upload} style={{ width: '32px', height: '32px', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }} />
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Click to upload video</span>
                <input type="file" accept="video/*" hidden onChange={(e) => e.target.files[0] && handleVideoUpload(e.target.files[0])} />
              </label>
            )}
            {uploadStatus === 'uploading' && (
              <div style={{ padding: '1.5rem', background: 'rgba(78,205,196,0.1)', borderRadius: '12px', textAlign: 'center' }}>
                <p style={{ marginBottom: '0.5rem' }}>Uploading... {uploadProgress}%</p>
                <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'linear-gradient(90deg, #4ecdc4, #44a08d)', transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            {uploadStatus === 'processing' && (
              <div style={{ padding: '1.5rem', background: 'rgba(254,202,87,0.1)', borderRadius: '12px', textAlign: 'center' }}>
                <Icon component={Loader2} style={{ width: '24px', height: '24px', color: '#feca57', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '0.5rem', color: '#feca57' }}>Processing video...</p>
              </div>
            )}
            {uploadStatus === 'ready' && (
              <div style={{ padding: '1.5rem', background: 'rgba(78,205,196,0.1)', borderRadius: '12px', textAlign: 'center' }}>
                <Icon component={CheckCircle2} style={{ width: '24px', height: '24px', color: '#4ecdc4' }} />
                <p style={{ marginTop: '0.5rem', color: '#4ecdc4' }}>Video ready!</p>
              </div>
            )}
          </div>

          {/* Title */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)' }}>Title</label>
            <input type="text" required value={formData.pilotTitle} onChange={(e) => setFormData({ ...formData, pilotTitle: e.target.value })}
              style={{ width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px', color: '#fff', fontSize: '1rem', boxSizing: 'border-box' }} />
          </div>

          {/* Logline */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)' }}>Logline</label>
            <textarea required rows={3} value={formData.logline} onChange={(e) => setFormData({ ...formData, logline: e.target.value })}
              style={{ width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px', color: '#fff', fontSize: '1rem', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          {/* Genre */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)' }}>Genre</label>
            <select required value={formData.genre} onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
              style={{ width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px', color: '#fff', fontSize: '1rem', boxSizing: 'border-box' }}>
              <option value="">Select genre</option>
              <option value="Comedy">Comedy</option>
              <option value="Drama">Drama</option>
              <option value="Reality TV">Reality TV</option>
              <option value="Stand Up">Stand Up</option>
            </select>
          </div>

          <button type="submit" disabled={submitting || uploadStatus !== 'ready'}
            style={{ width: '100%', padding: '0.9rem', background: 'none',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: 'rgba(255,255,255,0.45)',
              fontSize: '0.95rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
              cursor: (submitting || uploadStatus !== 'ready') ? 'not-allowed' : 'pointer',
              letterSpacing: '0.06em', transition: 'all 0.25s ease',
              opacity: (submitting || uploadStatus !== 'ready') ? 0.4 : 1 }}
            onMouseOver={e => { if (!submitting && uploadStatus === 'ready') { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}}
            onMouseOut={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
            {submitting ? 'Submitting...' : 'Submit Pilot'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreatorUploadNew;
